import React, { forwardRef } from "react";
import { Button, ButtonProps } from "@/shared/ui/Button";
import { Icon, IconColor, IconName, IconProps } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";

export interface IconButtonProps extends ButtonProps {
  icon: IconName;
  iconProps?: Omit<IconProps, "icon">;
  label?: string;
  loading?: boolean;
  as?: "button" | "a" | "span";
}

const IconButtonComponent = forwardRef<
  HTMLButtonElement,
  Omit<IconButtonProps, "icon">
>((props, ref) => {
  const { as, ...rest } = props;

  if (as === "span") {
    return (
      <span
        ref={ref}
        {...rest}
        className={cn("flex items-center p-2", rest.className)}
      />
    );
  }

  return <Button ref={ref} {...rest} />;
});

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (props, ref) => {
    const {
      icon,
      label,
      as = "button",
      color = "white",
      iconProps = {},
      className,
      children,
      loading,
      disabled,
      ...buttonProps
    } = props;

    function IconComponent() {
      if (loading) return <i className="far fa-spinner-third animate-spin" />;
      return (
        <Icon icon={icon} color={color as IconColor} size="sm" {...iconProps} />
      );
    }

    return (
      <IconButtonComponent
        as={as}
        ref={ref}
        className={cn("gap-2 relative", className)}
        disabled={loading || disabled}
        {...buttonProps}
      >
        <IconComponent />
        {label && <span className="">{label}</span>}
        {children}
      </IconButtonComponent>
    );
  }
);

export { IconButton };
